import { Component, HostListener, inject, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, filter, take } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import {
  ScoDialogModule,
  ScoCheckboxModule,
  ScoButtonModule,
  ScoChipsModule,
  ScoTabsModule,
  ScoBadgeModule,
} from '@schueco/schueco-ui-components';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LangRouterService } from '@frontend/util-router-history';

export type NewsTabType = 'changelog' | 'content';

export interface ChangelogEntry {
  id: string;
  published_date: string;
  version: string;
  content_type: string;
  title: string;
  content: {
    title: string;
    content: string;
  }[];
}

export interface NewsCategoryItem {
  id: number;
  ident: string;
  title: string;
  cover_image: string;
  type: string;
  date: number;
  seen: boolean;
}

export interface CategoryTreeNode {
  id: number;
  name: string;
  has_documents: boolean;
  childcategories?: CategoryTreeNode[];
}

@Component({
  selector: 'dc-util-info-dialog',
  templateUrl: './util-info-dialog.component.html',
  styleUrl: './util-info-dialog.component.css',
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    ScoDialogModule,
    ScoCheckboxModule,
    ScoButtonModule,
    ScoChipsModule,
    ScoTabsModule,
    ScoBadgeModule,
  ],
  standalone: true,
})
export class UtilInfoDialogComponent implements OnInit {
  dialogRef = inject<DialogRef<UtilInfoDialogComponent>>(DialogRef);
  data = inject<{
    url?: string;
    changelogEntries?: ChangelogEntry[];
    newsCategories?: NewsCategoryItem[];
    newsChangelogSeenAt?: number;
    newsContentSeenAt?: number;
    categoryTree?: CategoryTreeNode[];
    localStorageKey: string;
    onConfirm?: () => void;
    onLeaveNewsTab?: (newsType: NewsTabType) => void;
    onLeaveNewsDialog?: () => void;
    onCategorySeen?: (id: number) => void;
    newsSeenSuccess$?: Observable<NewsTabType>;
  }>(DIALOG_DATA);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private localize = inject(LangRouterService);

  isChecked = false;
  localStorageKey = '';
  enlargedImageSrc: string | null = null;
  activeTabId = 'changelog';
  changelogEntries: Array<{
    title: string;
    published_date: string;
    content_type: string;
    contentSections: Array<{ title: string; content: SafeHtml }>;
  }> = [];
  categories: NewsCategoryItem[] = [];
  changelogNewCount = 0;
  contentNewCount = 0;
  isMobileView = false;

  constructor() {
    const data = this.data;

    this.localStorageKey = data.localStorageKey;

    // Verarbeite Changelog-Einträge falls vorhanden
    if (data.changelogEntries) {
      const changelogSeenAt = data.newsChangelogSeenAt ?? 0;
      this.changelogNewCount = data.changelogEntries.filter(
        (entry) => Number(entry.published_date) > changelogSeenAt,
      ).length;
      this.changelogEntries = data.changelogEntries.map((entry) => ({
        title: entry.title,
        published_date: this.formatTimestamp(entry.published_date),
        content_type: entry.content_type,
        contentSections: entry.content.map((section) => ({
          title: section.title,
          content: this.sanitizer.bypassSecurityTrustHtml(section.content),
        })),
      }));
    }

    // Inhalte nach Datum absteigend sortiert anzeigen, neuester Eintrag zuerst
    if (data.newsCategories) {
      const seenAt = data.newsContentSeenAt ?? 0;
      this.categories = [...data.newsCategories].sort((a, b) => b.date - a.date);
      this.contentNewCount = data.newsCategories.filter((category) => category.date > seenAt).length;
    }

    // Badge ausblenden, sobald der Aufrufer das jeweilige Tab als gesehen bestätigt hat
    data.newsSeenSuccess$?.pipe(takeUntilDestroyed()).subscribe((newsType) => {
      if (newsType === 'changelog') {
        this.changelogNewCount = 0;
      } else if (newsType === 'content') {
        this.contentNewCount = 0;
      }
    });
  }

  ngOnInit() {
    this.checkScreenSize();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSizeAfterResize();
  }

  private checkScreenSize() {
    this.isMobileView = window.innerWidth <= 768;
  }

  private checkScreenSizeAfterResize() {
    if (window.innerWidth <= 768) {
      this.dialogRef.close();
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }

  private formatTimestamp(timestamp: string): string {
    // Unix-Timestamp in Sekunden -> Millisekunden für Date-Objekt
    const date = new Date(parseInt(timestamp) * 1000);
    const locale = navigator.language || 'de-DE';

    // Für deutsche Sprache: Format mit führenden Nullen
    if (locale.startsWith('de')) {
      return date.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    // Für andere Sprachen: Standard-Browser-Format
    return date.toLocaleDateString();
  }

  close() {
    this.markTabSeenAndNotifyLeave(this.activeTabId);
    if (this.localStorageKey) {
      if (this.isChecked && this.data.onConfirm) {
        this.data.onConfirm();
      }
    }
    this.dialogRef.close();
  }

  closeDialogClicked() {
    this.close();
  }

  onCheckboxChange() {
    this.isChecked = !this.isChecked;
  }

  onContentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const imgSrc = (target as HTMLImageElement).src;
      this.enlargedImageSrc = imgSrc;
    }
  }

  closeEnlargedImage() {
    this.enlargedImageSrc = null;
  }

  onTabChange(tabId: string) {
    if (tabId !== this.activeTabId) {
      this.markTabSeen(this.activeTabId);
    }
    this.activeTabId = tabId;
  }

  // Meldet das verlassene Tab als gesehen und blendet dessen Badge sofort aus, ohne auf setNewsSeenFlagSuccess zu warten
  private markTabSeen(tabId: string): NewsTabType | null {
    if (tabId === 'changelog' && this.changelogNewCount > 0) {
      this.changelogNewCount = 0;
      this.data.onLeaveNewsTab?.('changelog');
      return 'changelog';
    } else if (tabId === 'info' && this.contentNewCount > 0) {
      this.contentNewCount = 0;
      this.data.onLeaveNewsTab?.('content');
      return 'content';
    }
    return null;
  }

  // Ruft onLeaveNewsDialog erst auf, nachdem ein evtl. ausgelöstes setNewsSeenFlag erfolgreich gespeichert wurde
  private markTabSeenAndNotifyLeave(tabId: string) {
    const pendingType = this.markTabSeen(tabId);
    if (pendingType && this.data.newsSeenSuccess$) {
      this.data.newsSeenSuccess$
        .pipe(
          filter((type) => type === pendingType),
          take(1),
        )
        .subscribe(() => {
          this.data.onLeaveNewsDialog?.();
        });
    } else {
      this.data.onLeaveNewsDialog?.();
    }
  }

  getCategoryChipLabelKey(type: string): string {
    return type === 'new' ? 'NEWS_CONTENT.TYPE_NEW' : 'NEWS_CONTENT.TYPE_UPDATED';
  }

  formatCategoryDate(date: number): string {
    return this.formatTimestamp(date.toString());
  }

  getCategoryBreadcrumb(categoryId: number): string[] {
    const result: string[] = [];

    const traverse = (nodes: CategoryTreeNode[], ancestors: string[]): boolean => {
      for (const node of nodes) {
        const current = [...ancestors, node.name];
        if (node.id === categoryId) {
          result.push(...current);
          return true;
        }
        if (node.childcategories?.length && traverse(node.childcategories, current)) {
          return true;
        }
      }
      return false;
    };

    traverse(this.data.categoryTree ?? [], []);
    return result.slice(0, -1);
  }

  categoryLink(category: NewsCategoryItem): string {
    const node = this.findCategoryNode(category.id);
    return this.localize.translateRoute(
      '/categories/' + category.id + (node?.has_documents ? '/documents' : ''),
    ) as string;
  }

  onCategoryClick(category: NewsCategoryItem) {
    localStorage.removeItem('dc_sort_preference');
    const link = this.categoryLink(category) + '?sort=valid_from_desc&page=1';
    this.data.onCategorySeen?.(category.id);
    this.markTabSeenAndNotifyLeave(this.activeTabId);
    this.dialogRef.close();
    this.router.navigateByUrl(link);
  }

  private findCategoryNode(categoryId: number): CategoryTreeNode | undefined {
    const traverse = (nodes: CategoryTreeNode[]): CategoryTreeNode | undefined => {
      for (const node of nodes) {
        if (node.id === categoryId) {
          return node;
        }
        if (node.childcategories?.length) {
          const found = traverse(node.childcategories);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    return traverse(this.data.categoryTree ?? []);
  }
}
